import React from 'react';

import type { ButtonBaseProps } from '@mui/material';
import { alpha, ButtonBase, CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { progressPositionDetails } from 'src/components/generic';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  marginLeft: number;
  marginTop: number;
  variant: WhiteAlineButtonProps['variant'];
  disabled?: boolean;
  loading?: boolean;
}

const loadingAlphaVal = 0.75;
const useStyles = makeStyles<StyleProps>()(
  (theme, { disabled, loading, marginLeft, marginTop, variant }) => ({
    childText: {
      color: 'inherit',
      fontSize: 14,
      fontWeight: 600,
      opacity: disabled || loading ? loadingAlphaVal : undefined,
    },
    filled: (() => {
      const alphaVal = disabled || loading ? loadingAlphaVal : 1;
      const white = alpha(theme.palette.common.white, alphaVal);

      return {
        ...theme.appTypography.buttonSmall,
        '&:hover': {
          backgroundColor: white,
        },
        backgroundColor: white,
        borderRadius: 16,
        boxSizing: 'border-box' as const,
        fontSize: 14,
        height: 32,
        padding: theme.spacing(1, 2),
      };
    })(),
    outlined: (() => {
      const alphaVal = disabled || loading ? loadingAlphaVal : 1;
      const white = alpha(theme.palette.common.white, alphaVal);

      return {
        ...theme.appTypography.buttonSmall,
        '&:active': {
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          border: `${white} solid 1px`,
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          border: `${white} solid 1px`,
        },
        border: `1px solid ${white}`,
        borderRadius: 16,
        boxSizing: 'border-box' as const,
        color: 'inherit',
        fontSize: 14,
        height: 32,
        padding: theme.spacing(1, 2),
      };
    })(),
    progress: {
      left: '50%',
      marginLeft,
      marginTop,
      position: 'absolute' as const,
      top: '50%',
    },
    wrapper: (() => {
      const color =
        variant === 'outlined' ? theme.palette.common.white : theme.palette.primary.main;

      return {
        color: alpha(color, loading ? loadingAlphaVal : 1),
        position: 'relative' as const,
      };
    })(),
  })
);

interface WhiteAlineButtonProps extends ButtonBaseProps {
  loading?: boolean;
  variant?: 'filled' | 'outlined';
}

export function WhiteAlineButton({
  children,
  className,
  disabled,
  loading,
  variant = 'filled',
  ...rest
}: WhiteAlineButtonProps): JSX.Element {
  const { marginLeft, marginTop, progressSize } = progressPositionDetails('medium');
  const { classes } = useStyles({ disabled, loading, marginLeft, marginTop, variant });

  switch (variant) {
    case 'filled':
      return (
        <div className={clsx(classes.wrapper, className)}>
          <ButtonBase className={clsx(classes.filled)} disabled={loading || disabled} {...rest}>
            <Typography className={classes.childText}>{children}</Typography>
          </ButtonBase>
          {loading && (
            <CircularProgress className={classes.progress} color="inherit" size={progressSize} />
          )}
        </div>
      );
    case 'outlined':
      return (
        <div className={clsx(classes.wrapper, className)}>
          <ButtonBase className={clsx(classes.outlined)} disabled={loading || disabled} {...rest}>
            <Typography className={classes.childText}>{children}</Typography>
          </ButtonBase>
          {loading && (
            <CircularProgress className={classes.progress} color="inherit" size={progressSize} />
          )}
        </div>
      );
  }
}
